import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IoHeartOutline, IoHeart, IoChatbubbleOutline, IoShareOutline, IoEllipsisHorizontal, IoTrashOutline, IoCreateOutline, IoGlobeOutline, IoPeopleOutline, IoLockClosedOutline } from 'react-icons/io5';
import Avatar from './Avatar';
import CommentSection from './CommentSection';
import PostImageModal from './PostImageModal';
import LikeListModal from './LikeListModal';
import LikeTooltip from './LikeTooltip';
import LinkPreview from './LinkPreview';
import VoicePlayer from './VoicePlayer';
import SharePostModal from './SharePostModal';
import { aiApi } from '../services/aiApi';
import { useFloatingVideo } from '../contexts/FloatingVideoContext';
import { postApi } from '../services/postApi';
import { formatDistanceToNow, format } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import './PostCard.css';

// Helper function to render content with clickable @mentions
const renderContentWithMentions = (content) => {
  if (!content) return null;

  const parts = content.split(/(@[a-zA-Z0-9_.]+)/g);

  return parts.map((part, index) => {
    if (part.startsWith('@')) {
      const username = part.slice(1);
      return (
        <Link key={index} to={`/profile/${username}`} className="mention">
          {part}
        </Link>
      );
    }
    return part;
  });
};

const PostCard = ({ post, currentUserId, initialComments, onPostDeleted, onPostUpdated, onPostShared, highlightCommentId }) => {
  const { t, i18n } = useTranslation();
  const { floatingVideo, activateFloatingVideo, deactivateFloatingVideo } = useFloatingVideo();
  const [showMenu, setShowMenu] = useState(false);
  const commentSectionRef = useRef(null);
  const videoRefs = useRef({});
  const postRef = useRef(null);
  const menuRef = useRef(null);
  const floatingVideoRef = useRef(floatingVideo);
  const fetchTimeoutRef = useRef(null);
  const [isLiked, setIsLiked] = useState(post.user_has_liked);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [editLinkPreview, setEditLinkPreview] = useState(post.link_preview || null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [dismissedPreviewUrl, setDismissedPreviewUrl] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLikeList, setShowLikeList] = useState(false);
  const [showLikeTooltip, setShowLikeTooltip] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCount, setShareCount] = useState(post.share_count || 0);
  const [postVisibility, setPostVisibility] = useState(post.visibility || 'public');
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [subtitles, setSubtitles] = useState(null);
  const visibilityMenuRef = useRef(null);

  const locale = i18n.language === 'vi' ? vi : enUS;
  const isOwner = currentUserId === post.user_id;

  // Extract URLs from text (with or without http/https)
  const extractURL = useCallback((text) => {
    // First try to match URLs with protocol
    const urlWithProtocol = /(https?:\/\/[^\s]+)/g;
    let matches = text.match(urlWithProtocol);
    if (matches) return matches[0];

    // Then try to match URLs without protocol (e.g., example.com, www.example.com)
    const urlWithoutProtocol = /(?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?/g;
    matches = text.match(urlWithoutProtocol);
    if (matches) {
      // Prepend https:// if no protocol
      const url = matches[0];
      return url.startsWith('www.') || url.startsWith('http') ? url : 'https://' + url;
    }

    return null;
  }, []);

  // Fetch link preview
  const fetchLinkPreview = useCallback(async (url) => {
    if (!url) {
      setEditLinkPreview(null);
      return;
    }

    setIsLoadingPreview(true);
    try {
      const response = await postApi.fetchLinkPreview(url);
      if (response.data) {
        setEditLinkPreview(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch link preview:', error);
      // Silently fail - link preview is optional
    } finally {
      setIsLoadingPreview(false);
    }
  }, []);

  // Detect URLs in edit content and fetch preview (only when editing and no media files)
  useEffect(() => {
    if (!isEditing) {
      return;
    }

    // Clear previous timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Don't auto-fetch if there are media files
    if (post.media_urls && post.media_urls.length > 0) {
      if (editLinkPreview) {
        setEditLinkPreview(null);
      }
      return;
    }

    // Extract URL from content
    const url = extractURL(editContent);

    // If no URL in content, clear dismissed URL tracking
    if (!url && dismissedPreviewUrl) {
      setDismissedPreviewUrl(null);
    }

    // Don't auto-fetch if this URL was manually dismissed
    if (url && url === dismissedPreviewUrl) {
      return;
    }

    if (url && url !== editLinkPreview?.url) {
      // Debounce fetching to avoid too many requests
      fetchTimeoutRef.current = setTimeout(() => {
        fetchLinkPreview(url);
      }, 500);
    } else if (!url && editLinkPreview) {
      // Clear preview if URL was removed
      setEditLinkPreview(null);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [isEditing, editContent, post.media_urls, editLinkPreview, dismissedPreviewUrl, extractURL, fetchLinkPreview]);

  // Load subtitles when post has subtitle_url (prefer clean version)
  useEffect(() => {
    const subUrl = post.subtitle_url_clean || post.subtitle_url;
    if (!subUrl) {
      setSubtitles(null);
      return;
    }

    const loadSubtitles = async () => {
      try {
        const data = await aiApi.fetchSubtitles(subUrl);
        setSubtitles(data);
      } catch (err) {
        console.error('Failed to load subtitles:', err);
        setSubtitles(null);
      }
    };

    loadSubtitles();
  }, [post.subtitle_url_clean, post.subtitle_url]);

  const handleLike = async () => {
    try {
      if (isLiked) {
        await postApi.unlikePost(post.id);
        setIsLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        await postApi.likePost(post.id);
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('post.confirmDelete') || 'Are you sure you want to delete this post?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await postApi.deletePost(post.id);
      if (onPostDeleted) {
        onPostDeleted(post.id);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert(t('post.deleteError') || 'Failed to delete post');
    } finally {
      setIsDeleting(false);
      setShowMenu(false);
    }
  };

  const handleUpdate = async () => {
    if (!editContent.trim()) {
      return;
    }

    try {
      const updateData = {
        content: editContent,
        link_preview: editLinkPreview
      };
      const response = await postApi.updatePost(post.id, updateData);
      if (onPostUpdated) {
        onPostUpdated(response.data.data);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating post:', error);
      alert(t('post.updateError') || 'Failed to update post');
    }
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale });
    } catch {
      return timestamp;
    }
  };

  const formatExactTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'PPpp', { locale });
    } catch {
      return timestamp;
    }
  };

  const visibilityLabel = {
    public: t('post.visibilityPublic') || 'Public',
    friends: t('post.visibilityFriends') || 'Friends',
    only_me: t('post.visibilityOnlyMe') || 'Only me',
  };

  const visibilityIconMap = {
    public: IoGlobeOutline,
    friends: IoPeopleOutline,
    only_me: IoLockClosedOutline,
  };

  const handleVisibilityChange = async (newVisibility) => {
    setShowVisibilityMenu(false);
    if (newVisibility === postVisibility) return;
    const prevVisibility = postVisibility;
    setPostVisibility(newVisibility);
    try {
      await postApi.updatePost(post.id, { content: post.content || '', visibility: newVisibility, link_preview: post.link_preview || null });
    } catch (error) {
      console.error('Error updating visibility:', error);
      setPostVisibility(prevVisibility);
    }
  };

  // Close visibility menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (visibilityMenuRef.current && !visibilityMenuRef.current.contains(event.target)) {
        setShowVisibilityMenu(false);
      }
    };
    if (showVisibilityMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVisibilityMenu]);

  const handleCommentAdded = () => {
    setCommentCount(prev => prev + 1);
  };

  const handleCommentClick = () => {
    if (!showComments) {
      setShowComments(true);
      setTimeout(() => {
        if (commentSectionRef.current) {
          commentSectionRef.current.focusInput();
        }
      }, 100);
    } else {
      setShowComments(false);
    }
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const handleLikeCountClick = () => {
    if (likeCount > 0) {
      setShowLikeList(true);
    }
  };

  // Update ref when floatingVideo changes
  useEffect(() => {
    floatingVideoRef.current = floatingVideo;
  }, [floatingVideo]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Track video visibility and activate floating player when scrolled out of view
  useEffect(() => {
    const videoElements = Object.values(videoRefs.current).filter(v => v !== null);
    if (videoElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const videoIndex = parseInt(video.dataset.index);
          const videoUrl = post.media_urls[videoIndex];
          const currentFloating = floatingVideoRef.current;

          // If video is playing and scrolls out of view, activate floating player
          // Only activate if not already in floating mode
          if (!entry.isIntersecting && !video.paused && currentFloating?.url !== videoUrl) {
            activateFloatingVideo({
              postId: post.id,
              url: videoUrl,
              author: post.user,
              caption: post.content,
              currentTime: video.currentTime
            });
          }

          // If this post's video is in floating mode and scrolls back into view, deactivate floating
          if (entry.isIntersecting && currentFloating?.postId === post.id && currentFloating?.url === videoUrl) {
            const currentTime = currentFloating.currentTime;
            deactivateFloatingVideo();
            // Resume playback in the feed
            setTimeout(() => {
              if (video && currentTime) {
                video.currentTime = currentTime;
                video.play().catch(err => console.error('Error resuming video:', err));
              }
            }, 100);
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of video is out of view
        rootMargin: '0px'
      }
    );

    videoElements.forEach(video => {
      if (video) observer.observe(video);
    });

    return () => {
      videoElements.forEach(video => {
        if (video) observer.unobserve(video);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  // Pause local video when floating video is active for this post
  useEffect(() => {
    if (floatingVideo?.postId === post.id) {
      const video = videoRefs.current[floatingVideo.url];
      if (video && !video.paused) {
        video.pause();
      }
    }
  }, [floatingVideo, post.id]);

  return (
    <div className="post-card" id={`post-${post.id}`} ref={postRef}>
      <div className="post-header">
        <div className="post-author">
          <Link to={`/profile/${post.user?.id}`} className="post-author-link">
            <Avatar user={post.user} size="medium" />
            <div className="author-info">
              <div className="author-names-row">
                <span className="author-name">
                  {post.user?.display_name}
                  {post.shared_post_id && <span className="shared-label"> {t('post.sharedPost') || 'shared a post'}</span>}
                </span>
                <span className="author-username">@{post.user?.username}</span>
                <span className="post-time-text mobile-only" data-tooltip={formatExactTime(post.created_at)}>{formatTime(post.created_at)}</span>
              </div>
            </div>
          </Link>
        </div>
        <div className="post-meta">
          <span className="post-time-text" data-tooltip={formatExactTime(post.created_at)}>{formatTime(post.created_at)}</span>
          {postVisibility && visibilityIconMap[postVisibility] && (
            isOwner ? (
              <span className="visibility-badge clickable" ref={visibilityMenuRef}>
                <span
                  className="visibility-badge-icon"
                  onClick={() => setShowVisibilityMenu(!showVisibilityMenu)}
                  data-tooltip={visibilityLabel[postVisibility]}
                >
                  {React.createElement(visibilityIconMap[postVisibility])}
                </span>
                {showVisibilityMenu && (
                  <div className="visibility-quick-menu">
                    {Object.entries(visibilityIconMap).map(([value, Icon]) => (
                      <button
                        key={value}
                        className={`visibility-quick-option ${value === postVisibility ? 'active' : ''}`}
                        onClick={() => handleVisibilityChange(value)}
                      >
                        <Icon />
                      </button>
                    ))}
                  </div>
                )}
              </span>
            ) : (
              <span className="visibility-badge" data-tooltip={visibilityLabel[postVisibility]}>
                {React.createElement(visibilityIconMap[postVisibility])}
              </span>
            )
          )}
        </div>
        {isOwner && (
          <div className="post-menu" ref={menuRef}>
            <button className="icon-button" onClick={() => setShowMenu(!showMenu)}>
              <IoEllipsisHorizontal />
            </button>
            {showMenu && (
              <div className="post-menu-dropdown">
                <button onClick={() => {
                  setIsEditing(true);
                  setEditLinkPreview(post.link_preview || null);
                  setDismissedPreviewUrl(null);
                  setShowMenu(false);
                }}>
                  <IoCreateOutline /> {t('post.edit') || 'Edit'}
                </button>
                <button onClick={handleDelete} disabled={isDeleting} className="danger">
                  <IoTrashOutline /> {isDeleting ? t('post.deleting') || 'Deleting...' : t('post.delete') || 'Delete'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="post-content">
        {isEditing ? (
          <div className="post-edit-form">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="post-edit-textarea"
              rows="3"
            />
            {isLoadingPreview && (
              <div className="link-preview-loading">Loading preview...</div>
            )}
            {editLinkPreview && !isLoadingPreview && (
              <LinkPreview
                preview={editLinkPreview}
                onRemove={() => {
                  setDismissedPreviewUrl(editLinkPreview.url); // Track dismissed URL
                  setEditLinkPreview(null);
                }}
              />
            )}
            <div className="post-edit-actions">
              <button className="btn btn-text" onClick={() => setIsEditing(false)}>
                {t('common.cancel') || 'Cancel'}
              </button>
              <button className="btn btn-primary" onClick={handleUpdate}>
                {t('common.save') || 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <p className="post-text">{renderContentWithMentions(post.content)}</p>
        )}

        {post.voice_url && post.voice_duration && (
          <VoicePlayer voiceUrl={post.voice_url_clean || post.voice_url} duration={post.voice_duration} subtitles={subtitles} />
        )}

        {post.media_urls && post.media_urls.length > 0 && (
          <div className={`post-media grid-${Math.min(post.media_urls.length, 4)}`}>
            {post.media_urls.map((url, index) => (
                <div key={index} className="media-item" onClick={() => handleImageClick(index)}>
                  {post.media_types[index] === 'video' ? (
                    <video
                      ref={el => videoRefs.current[url] = el}
                      data-index={index}
                      src={url}
                      controls
                      preload="metadata"
                      playsInline
                      onClick={(e) => e.stopPropagation()}
                      onError={(e) => console.error('Video error:', e.target.error, 'URL:', url)}
                    />
                  ) : (
                    <img src={url} alt={`Post media ${index + 1}`} />
                  )}
                </div>
            ))}
          </div>
        )}

        {!isEditing && post.link_preview && <LinkPreview preview={post.link_preview} />}

        {/* Shared post embed */}
        {post.shared_post_id && (
          post.shared_post ? (
            <div className="shared-post-embed">
              <Link to={`/profile/${post.shared_post.user?.id}`} className="shared-post-header">
                <Avatar user={post.shared_post.user} size="small" />
                <div className="shared-post-author-info">
                  <span className="shared-post-name">{post.shared_post.user?.display_name}</span>
                  <span className="shared-post-time">{formatTime(post.shared_post.created_at)}</span>
                </div>
              </Link>
              {post.shared_post.content && (
                <p className="shared-post-text">{renderContentWithMentions(post.shared_post.content)}</p>
              )}
              {post.shared_post.voice_url && post.shared_post.voice_duration && (
                <VoicePlayer voiceUrl={post.shared_post.voice_url_clean || post.shared_post.voice_url} duration={post.shared_post.voice_duration} compact={true} />
              )}
              {post.shared_post.media_urls && post.shared_post.media_urls.length > 0 && (
                <div className={`post-media grid-${Math.min(post.shared_post.media_urls.length, 4)}`}>
                  {post.shared_post.media_urls.map((url, index) => (
                    <div key={index} className="media-item">
                      {post.shared_post.media_types[index] === 'video' ? (
                        <video src={url} controls preload="metadata" playsInline />
                      ) : (
                        <img src={url} alt={`Shared media ${index + 1}`} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {post.shared_post.link_preview && <LinkPreview preview={post.shared_post.link_preview} />}
            </div>
          ) : (
            <div className="shared-post-deleted">
              {t('post.originalDeleted') || 'Original post has been deleted'}
            </div>
          )
        )}
      </div>

      <div className="post-actions">
        <button
          className={`action-button ${isLiked ? 'liked' : ''}`}
          onClick={handleLike}
        >
          {isLiked ? <IoHeart /> : <IoHeartOutline />}
          {likeCount > 0 && <span className="action-count">{likeCount}</span>}
        </button>
        <button className="action-button" onClick={handleCommentClick}>
          <IoChatbubbleOutline />
          {commentCount > 0 && <span className="action-count">{commentCount}</span>}
        </button>
        <button className="action-button" onClick={() => setShowShareModal(true)}>
          <IoShareOutline />
          {shareCount > 0 && <span className="action-count">{shareCount}</span>}
        </button>
      </div>

      <div className={`comment-section-wrapper ${showComments ? 'visible' : ''}`}>
        <CommentSection
          ref={commentSectionRef}
          postId={post.id}
          initialComments={initialComments}
          onCommentAdded={handleCommentAdded}
          initialVisible={3}
          highlightCommentId={highlightCommentId}
        />
      </div>

      {showImageModal && (
        <PostImageModal
          post={post}
          currentUserId={currentUserId}
          imageIndex={selectedImageIndex}
          onClose={() => setShowImageModal(false)}
          onPostUpdated={onPostUpdated}
        />
      )}

      {showLikeList && (
        <LikeListModal
          postId={post.id}
          onClose={() => setShowLikeList(false)}
        />
      )}

      {showShareModal && (
        <SharePostModal
          post={post}
          onClose={() => setShowShareModal(false)}
          onShared={() => {
            setShareCount(prev => prev + 1);
            if (onPostShared) onPostShared();
          }}
        />
      )}
    </div>
  );
};

export default PostCard;
